import React, {ReactNode, useState} from "react";
import {
    HttpError,
    useDeleteMany,
    useTranslate,
    useUpdateMany,
    useApiUrl,
    useNotification,
    useInvalidate,
} from "@refinedev/core";
import {useForm} from "@refinedev/react-hook-form";
import {IOrder, IUser, ICustomer} from "../../../interfaces";
import {axiosInstance} from "../../../authProvider";
import {EditFieldManager} from "../../../components/edit/fields";
import {
    Box,
    Button, ButtonGroup, ClickAwayListener,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, Grow, LinearProgress, ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popper,
    Typography,
} from "@mui/material";
import {
    ArrowDropDown,
    Check, Delete,
    DeleteForever,
    LocalShippingOutlined, NoCrash,
} from "@mui/icons-material";
import {ConfirmDialog} from "components/ConfirmDialog";

export type OrderStatus =
    | "draft"
    | "pending"
    | "confirmed"
    | "delivered"
    | "cancelled";
export type OrderActionsProps = {
    buttonsBefore?: ReactNode
    status: OrderStatus;
    selectedIds?: string[];
    disabled: boolean;
};
export type OrderActionsModalProps = {
    title: string;
    fields: (fieldManager: EditFieldManager<IOrder>) => JSX.Element;
    open: boolean;
    confirmText?: string | React.ReactNode
    //keyof IOrder
    onClose: () => void;
    onSubmit: (order: Partial<IOrder>) => void;
};

export type ModalState = {
    fields: (fieldManager: EditFieldManager<IOrder>) => JSX.Element;
    open: boolean;
    title: string;
    onSubmit: (order: Partial<IOrder>) => void;
    confirmText?: string | React.ReactNode;

};
export const OrderActionsModal: React.FC<OrderActionsModalProps> = ({
                                                                        title,
                                                                        fields,
                                                                        open,
                                                                        confirmText,
                                                                        onClose,
                                                                        onSubmit,
                                                                    }) => {
    const translate = useTranslate();

    const {
        register,
        //handleSubmit,
        formState: {errors},
        control,
        getValues,
        reset,
    } = useForm<IOrder, HttpError, IOrder>({});

    const fieldManager = new EditFieldManager(
        control,
        register,
        undefined,
        errors
    );

    const handleSubmit = () => {
        onSubmit(getValues());
        onClose();
        reset();
    };
    const handleClose = () => {
        onClose();
        reset();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{sx: {minWidth: 320}}}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box
                    component="form"
                    autoComplete="off"
                    sx={{display: "flex", flexDirection: "column"}}
                >
                    {fields(fieldManager)}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="error">
                    {translate("buttons.cancel", "Cancel")}
                </Button>
                <Button onClick={handleSubmit} color="success" variant="contained">
                    <Check/>
                    <Typography variant="button">
                        {confirmText || translate("orders.actions.confirm", "Confirm orders")}
                    </Typography>
                </Button>
            </DialogActions>
        </Dialog>
    );
};
export const OrderActions: React.FC<OrderActionsProps> = ({
                                                              status,
                                                              disabled,
                                                              selectedIds,
                                                              buttonsBefore
                                                          }) => {
    const translate = useTranslate();
    const apiUrl = useApiUrl();
    const {open: openNotification} = useNotification();
    const invalidate = useInvalidate();
    const [modalState, setModalState] = useState<ModalState>({
        open: false,
        title: "",
        fields: (fieldManager) => <></>,
        onSubmit: (order) => {
        },
    });
    const [showConfirm, setShowConfirm] = useState(false);

    const [showMenu, setShowMenu] = useState(false)
    const menuAnchorRef = React.useRef<HTMLDivElement>(null);

    // Progress dialog state
    const [progressDialog, setProgressDialog] = useState({
        open: false,
        total: 0,
        confirmed: 0,
        documentsQueued: 0,
        errors: 0,
    });

    const {mutate, isLoading} = useUpdateMany<IOrder>();
    const {mutate: deleteMany} = useDeleteMany<IOrder>();

    const selectDriver = (driverId: string) => {
        if (!driverId) {
            return;
        }
        mutate(
            {
                resource: "orders",
                meta: {method: "put"},
                values: {
                    status: status,
                    driver_id: driverId,
                },
                ids: selectedIds || [],
            },
            {
                onError: (error, variables, context) => {
                    //notify("custom.orders_confirmed_error", "warning")
                    // An error occurred!
                },
                onSuccess: (data, variables, context) => {
                    // Let's celebrate!
                },
            }
        );
    };
    const unassignDriver = () => {
        mutate(
            {
                resource: "orders",
                meta: {method: "put"},
                values: {
                    unassign_driver: true,
                },
                ids: selectedIds || [],
            },
            {
                onError: (error, variables, context) => {
                    //notify("custom.orders_confirmed_error", "warning")
                    // An error occurred!
                },
                onSuccess: (data, variables, context) => {
                    // Let's celebrate!
                },
            }
        );
    };
    const confirmOrders = async (checkedShipox?: boolean, checkedFatture?: boolean, createDocument?: boolean) => {
        const orderIds = selectedIds || [];
        const totalOrders = orderIds.length;

        // Show progress dialog
        setProgressDialog({
            open: true,
            total: totalOrders,
            confirmed: 0,
            documentsQueued: 0,
            errors: 0,
        });

        let confirmedCount = 0;
        let confirmErrors = 0;

        try {
            // Confirm orders in batches of 10 to avoid overwhelming the server
            const confirmBatchSize = 10;

            for (let i = 0; i < orderIds.length; i += confirmBatchSize) {
                const batch = orderIds.slice(i, i + confirmBatchSize);

                const confirmPromises = batch.map(orderId =>
                    axiosInstance.put(`${apiUrl}/orders/${orderId}`, {
                        status: "confirmed",
                        update_shipox: checkedShipox || false,
                        update_fatture: checkedFatture || false,
                    })
                    .then(() => {
                        confirmedCount++;
                        setProgressDialog(prev => ({...prev, confirmed: confirmedCount}));
                    })
                    .catch(error => {
                        console.error(`Error confirming order ${orderId}:`, error);
                        confirmErrors++;
                        setProgressDialog(prev => ({...prev, errors: prev.errors + 1}));
                    })
                );

                await Promise.all(confirmPromises);

                // Small delay between batches
                if (i + confirmBatchSize < orderIds.length) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }

            // After confirming orders, queue documents for generation
            let successCount = 0;
            let errorCount = 0;
            const docBatchSize = 5;

            for (let i = 0; i < orderIds.length; i += docBatchSize) {
                const batch = orderIds.slice(i, i + docBatchSize);

                const batchPromises = batch.map(orderId =>
                    axiosInstance.get(`${apiUrl}/orders/document/${orderId}`)
                        .then(() => {
                            successCount++;
                            setProgressDialog(prev => ({...prev, documentsQueued: successCount}));
                        })
                        .catch(error => {
                            console.error(`Error queuing document for order ${orderId}:`, error);
                            errorCount++;
                        })
                );

                await Promise.all(batchPromises);

                if (i + docBatchSize < orderIds.length) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            // Keep dialog open for 2 seconds to show final status
            setTimeout(() => {
                setProgressDialog(prev => ({...prev, open: false}));
                openNotification?.({
                    type: confirmErrors > 0 ? "error" : "success",
                    message: "Ordini confermati",
                    description: `${confirmedCount} ordini confermati, ${confirmErrors} errori. ${successCount} documenti accodati.`,
                });
                invalidate({
                    resource: "orders",
                    invalidates: ["list"],
                });
            }, 2000);

        } catch (error) {
            console.error("Error in confirmOrders:", error);
            setProgressDialog(prev => ({...prev, open: false}));
            openNotification?.({
                type: "error",
                message: "Errore durante la conferma",
                description: error instanceof Error ? error.message : "Errore sconosciuto",
            });
        }
    };
    const deliverOrders = () => {
        mutate(
            {
                resource: "orders",
                meta: {method: "put"},
                values: {
                    status: "delivered",
                },
                ids: selectedIds || [],
            },
            {
                onError: (error, variables, context) => {
                    //notify("custom.orders_confirmed_error", "warning")
                    // An error occurred!
                },
                onSuccess: (data, variables, context) => {
                    // Let's celebrate!
                },
            }
        );
    };
    const deleteOrders = (confirm: boolean) => {
        setShowConfirm(false);
        if (!confirm) {
            return;
        }
        deleteMany(
            {
                resource: "orders",
                ids: selectedIds || [],
            },
            {
                onError: (error, variables, context) => {
                    //notify("custom.orders_confirmed_error", "warning")
                    // An error occurred!
                },
                onSuccess: (data, variables, context) => {
                    // Let's celebrate!
                },
            }
        );
    };

    const resetModalState = () => {
        setModalState({
            open: false,
            title: "",
            fields: (fieldManager) => <></>,
            onSubmit: (order) => {
            },
        });
    };

    const orderConfirmButton = (
        <Button
            variant="contained"
            color="success"
            disabled={disabled || isLoading}
            onClick={() => {
                setModalState({
                    open: true,
                    title: translate("orders.actions.confirm", "Confirm orders"),
                    fields: (fieldManager) => {
                        return (
                            <>
                                {fieldManager.switchField(
                                    "update_shipox",
                                    translate("orders.fields.update_shipox", "Update Shipox")
                                )}
                                {fieldManager.switchField(
                                    "update_fatture",
                                    translate("orders.fields.update_fatture", "Update fatture"),
                                    true
                                )}
                                {fieldManager.switchField(
                                    "create_order_document",
                                    translate("orders.fields.create_order_document", "Crea documento d'ordine"),
                                    true
                                )}
                            </>
                        );
                    },
                    onSubmit: (order) => {
                        confirmOrders(order.update_shipox, order.update_fatture, order.create_order_document);
                    },
                });
            }}
            startIcon={<Check/>}
        >
            {translate("orders.actions.confirm", "Confirm orders")}
        </Button>
    );

    const orderDeleteButton = (
        <Button
            variant="contained"
            color="error"
            disabled={disabled || isLoading}
            onClick={() => {
                setShowConfirm(true);
            }}
            startIcon={<DeleteForever/>}
        >
            {translate("orders.actions.delete", "Delete orders")}
        </Button>
    );

    const orderAssignButton = (
        <>
            <ButtonGroup variant="contained" ref={menuAnchorRef}>
                <Button
                    color="success"
                    variant="contained"
                    disabled={disabled || isLoading}
                    onClick={() => {
                        setModalState({
                            open: true,
                            title: translate("orders.actions.assign_driver", "Assign driver"),
                            fields: (fieldManager) => {

                                return (
                                    <>
                                        {fieldManager.autocompleteField<IUser>(
                                            "driver_id",
                                            translate("orders.fields.driver", "Driver"),
                                            "users",
                                            "id",
                                            "name",
                                            [
                                                {
                                                    field: "driver_role",
                                                    operator: "startswith",
                                                    value: "Driver",
                                                },
                                                {
                                                    field: "name",
                                                    // @ts-ignore
                                                    operator: "isNullOrNot",
                                                    value: "notNull",
                                                },
                                            ]
                                        )}
                                    </>
                                );
                            },
                            onSubmit: (order) => {
                                if (order.driver_id) {
                                    selectDriver(order.driver_id);
                                } else {
                                    unassignDriver()
                                }
                            },
                        });
                    }}
                    startIcon={<LocalShippingOutlined/>}
                >
                    {translate("orders.actions.assign", "Assign to driver")}
                </Button>
                <Button size="small" disabled={disabled || isLoading} color="success" onClick={() => {
                    setShowMenu((prevOpen) => !prevOpen)
                }}>
                    <ArrowDropDown/>
                </Button>
            </ButtonGroup>
            <Popper
                sx={{
                    zIndex: 1,
                }}
                open={showMenu}
                anchorEl={menuAnchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom' ? 'center top' : 'center bottom',
                        }}
                    >
                        <Paper sx={{zIndex: 1000}}>
                            <ClickAwayListener onClickAway={() => {
                            }}>
                                <MenuList id="split-button-menu" sx={{minWidth: '200px', zIndex: 1000}} autoFocusItem>
                                    <MenuItem sx={{zIndex: 1000}} disabled={disabled || isLoading} onClick={() => {
                                        setShowMenu(false);
                                        unassignDriver()
                                    }}>
                                        <ListItemIcon>
                                            <Delete/>
                                        </ListItemIcon>
                                        <ListItemText color="error">Unassign driver</ListItemText>
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );


    const orderDeliverButton = (
        <Button
            color="success"
            variant="contained"
            disabled={disabled || isLoading}
            onClick={() => {
                setModalState({
                    open: true,
                    title: translate("orders.actions.confirm_delivery", "Confirm delivery?"),
                    confirmText: translate("orders.actions.confirm", "Confirm"),
                    fields: (fieldManager) => {
                        return (
                            <>
                            </>
                        );
                    },
                    onSubmit: (order) => {
                        return deliverOrders()
                    },
                });
            }}
            startIcon={<NoCrash/>}
        >
            {translate("orders.actions.deliver", "Deliver")}
        </Button>
    );

    // Progress Dialog Component
    const ProgressDialog = (
        <Dialog
            open={progressDialog.open}
            PaperProps={{sx: {minWidth: 400}}}
        >
            <DialogTitle>Conferma ordini in corso...</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2, py: 2}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                        <CircularProgress size={40}/>
                        <Box sx={{flex: 1}}>
                            <Typography variant="body2" color="text.secondary">
                                Ordini confermati: {progressDialog.confirmed} / {progressDialog.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Documenti accodati: {progressDialog.documentsQueued} / {progressDialog.total}
                            </Typography>
                            {progressDialog.errors > 0 && (
                                <Typography variant="body2" color="error">
                                    Errori: {progressDialog.errors}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={(progressDialog.documentsQueued / progressDialog.total) * 100}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Puoi continuare a lavorare mentre gli ordini vengono processati
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );

    switch (status) {
        case "draft":
            return (
                <>
                    {buttonsBefore}
                    {orderDeleteButton}
                    {orderConfirmButton}
                    <OrderActionsModal
                        title={modalState.title}
                        fields={modalState.fields}
                        open={modalState.open}
                        onSubmit={modalState.onSubmit}
                        onClose={resetModalState}
                    />
                    <ConfirmDialog open={showConfirm} onClose={deleteOrders}/>
                    {ProgressDialog}
                </>
            );
        case "pending":
            return (
                <>
                    {buttonsBefore}
                    {orderConfirmButton}
                    {orderAssignButton}
                    <OrderActionsModal
                        title={modalState.title}
                        fields={modalState.fields}
                        open={modalState.open}
                        onSubmit={modalState.onSubmit}
                        onClose={resetModalState}
                    />
                    {ProgressDialog}
                </>
            );
        case "confirmed":
            return (
                <>
                    {buttonsBefore}
                    {orderAssignButton}
                    {orderDeliverButton}
                    <OrderActionsModal
                        title={modalState.title}
                        fields={modalState.fields}
                        open={modalState.open}
                        confirmText={modalState.confirmText}
                        onSubmit={modalState.onSubmit}
                        onClose={resetModalState}
                    />
                    {ProgressDialog}
                </>
            );
        case "cancelled":
            return (
                <>
                    {buttonsBefore}
                    {orderDeleteButton}
                    <ConfirmDialog open={showConfirm} onClose={deleteOrders}/>
                    {ProgressDialog}
                </>
            );
    }
    return <>{buttonsBefore}{ProgressDialog}</>;
};
